// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect } from "react";
import { X, ChevronDown, Save, Settings } from "lucide-react";
import { productService } from "../../../services/productService";
import MultiSelectFilter from "./MultiSelectFilter";
import {
  SCENARIO_OPTIONS,
  PACKAGE_OPTIONS,
} from "../../../utils/scenarioConfig";

const ProductDetailView = ({ product, isOpen, onClose, onEdit }) => {
  const [editedProduct, setEditedProduct] = useState({});
  const [isExpanded, setIsExpanded] = useState(true);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [scenarioDropdownOpen, setScenarioDropdownOpen] = useState(false);
  const [minConfigModalOpen, setMinConfigModalOpen] = useState(false);
  const [maxConfigModalOpen, setMaxConfigModalOpen] = useState(false);
  const [minConfigurations, setMinConfigurations] = useState([]);
  const [maxConfigurations, setMaxConfigurations] = useState([]);
  const [minDefault, setMinDefault] = useState(0);
  const [maxDefault, setMaxDefault] = useState(0);

  const [productTypes, setProductTypes] = useState([]);
  const [loadingProductTypes, setLoadingProductTypes] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [applicableForOptions, setApplicableForOptions] = useState([]);
  const [loadingApplicableFor, setLoadingApplicableFor] = useState(false);

  const [packageOptions, setPackageOptions] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [packageDropdownOpen, setPackageDropdownOpen] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Update editedProduct when product changes
  useEffect(() => {
    if (product) {
      setEditedProduct({ ...product });
      // Reset save states when product changes
      setSaveError(null);
      setSaveSuccess(false);
    }
  }, [product]);

  // Fetch all dynamic data when component mounts
  useEffect(() => {
    const fetchData = async () => {
      setLoadingLocations(true);
      setLoadingScenarios(true);
      setLoadingProductTypes(true);
      setLoadingCategories(true);
      setLoadingStatus(true);
      setLoadingApplicableFor(true);
      setLoadingPackages(true);

      try {
        const filtersData = await productService.getAllFilters();
        setLocations(filtersData.locations || []);

        const scenarioOptions = (filtersData.scenarios || []).map(
          (scenario) => ({
            value: scenario.scenario_code || scenario.name,
            label: scenario.name || scenario.scenario_code || "Unknown",
          }),
        );
        setScenarios(scenarioOptions);

        // Process product types for dropdown
        const productTypeOptions = (filtersData.productTypes || []).map(
          (type) => ({
            value: type.id,
            label: type.name,
          }),
        );
        setProductTypes(productTypeOptions);

        // Process categories for dropdown
        const categoryOptions = (filtersData.categories || []).map(
          (category) => ({
            value: category.id,
            label: category.name,
          }),
        );
        setCategories(categoryOptions);

        // Process status options for dropdown
        const statusOptionsData = (filtersData.status || []).map((status) => ({
          value: status.id,
          label: status.name,
        }));
        setStatusOptions(statusOptionsData);

        // Process applicable for options
        const applicableForOptionsData = (
          filtersData.applicableForOptions || []
        ).map((option) => ({
          value: option.id,
          label: option.name,
        }));
        setApplicableForOptions(applicableForOptionsData);

        // Process package options for multi-select; API enum is S,M,L only.
        // Exclude "none" from API if present, then add single "None (no package)" (API receives []).
        const apiPaket = filtersData.paketOptions || [];
        const packageOptionsData = apiPaket
          .filter((pkg) => pkg !== "none")
          .map((pkg) => ({ value: pkg, label: pkg }));
        setPackageOptions([
          ...packageOptionsData,
          { value: "none", label: "None (no package)" },
        ]);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLocations([]);
        setScenarios([]);
        setProductTypes([]);
        setCategories([]);
        setStatusOptions([]);
        setApplicableForOptions([]);
        setPackageOptions([]);
      } finally {
        setLoadingLocations(false);
        setLoadingScenarios(false);
        setLoadingProductTypes(false);
        setLoadingCategories(false);
        setLoadingStatus(false);
        setLoadingApplicableFor(false);
        setLoadingPackages(false);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  if (!isOpen || !product) return null;

  const handleInputChange = (field, value) => {
    // Auto-calc brutto from net when editing net price; keep brutto editable otherwise
    if (field === "vhp_net_price") {
      const net = parseFloat(value);
      const gross = isNaN(net) ? "" : (net * 1.19).toFixed(2);
      setEditedProduct((prev) => ({
        ...prev,
        vhp_net_price: value,
        bruttopreis_pro_einheit: gross,
      }));
    } else {
      setEditedProduct((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
    // Clear save error when user makes changes
    if (saveError) setSaveError(null);
  };

  const handleScenarioSelectionChange = (selectedValues) => {
    handleInputChange("bookable_for_scenarios", selectedValues);
  };

  const handleScenarioDropdownToggle = () => {
    setScenarioDropdownOpen(!scenarioDropdownOpen);
  };

  // Add package selection handlers
  const handlePackageSelectionChange = (selectedValues) => {
    handleInputChange("paket", selectedValues);
  };

  const handlePackageDropdownToggle = () => {
    setPackageDropdownOpen(!packageDropdownOpen);
  };

  const handleSave = async () => {
    if (!editedProduct.id) {
      setSaveError("Product ID is missing. Cannot update product.");
      return;
    }

    // Validation according to schema
    const errors = {};
    const allowedStatuses = ["active", "inactive", "archived", "fordeletion"];

    const nameValue = (editedProduct.vhp_name || "").trim();
    if (!nameValue) {
      errors.name = "Name is required";
    } else if (nameValue.length < 3) {
      errors.name = "Name must be at least 3 characters";
    } else if (nameValue.length > 100) {
      errors.name = "Name must be at most 100 characters";
    }

    const skuValue = String(editedProduct.vhp_sku || "").trim();
    if (!skuValue) {
      errors.sku = "SKU is required";
    } else if (!/^[A-Z0-9-]+$/.test(skuValue)) {
      errors.sku = "SKU must be A-Z, 0-9 or '-' only";
    }

    const statusValue = editedProduct.vhp_archiving_flag;
    if (!statusValue) {
      errors.status = "Status is required";
    } else if (!allowedStatuses.includes(statusValue)) {
      errors.status = "Status must be active, inactive or archived";
    }

    const priceRaw = editedProduct.bruttopreis_pro_einheit;
    const priceValue =
      priceRaw === "" || priceRaw === null ? NaN : Number(priceRaw);
    if (Number.isNaN(priceValue)) {
      errors.prices = "Price must be a number";
    } else if (priceValue < 0) {
      errors.prices = "Price must be non-negative";
    } else if (priceValue > 999999.99) {
      errors.prices = "Price must be ≤ 999,999.99";
    }

    // Quantities: validate defaults if present (must be non-negative integers).
    // 0 is a valid default meaning "not set" / "applies to all via standardwert".
    const minDefaultVal =
      typeof editedProduct?.mindestabnahme === "object"
        ? editedProduct.mindestabnahme.default
        : editedProduct?.mindestabnahme;
    const maxDefaultVal =
      typeof editedProduct?.maximalmenge === "object"
        ? editedProduct.maximalmenge.default
        : editedProduct?.maximalmenge;

    const quantitiesToCheck = [minDefaultVal, maxDefaultVal].filter(
      (v) => v !== undefined && v !== null && v !== "",
    );
    if (quantitiesToCheck.length > 0) {
      const invalid = quantitiesToCheck.some((q) => {
        const n = Number(q);
        return !Number.isFinite(n) || n < 0 || !Number.isInteger(n);
      });
      if (invalid) {
        errors.quantities = "Quantities must be non-negative integers";
      }
    }

    // Mandatory (Pflicht) items must never be zero — mirror the API rule.
    if (
      editedProduct.layout_position === "Pflicht" &&
      (Number(minDefaultVal) || 0) < 1
    ) {
      errors.mindestabnahme =
        "Pflicht-Artikel müssen eine Mindestabnahme von mindestens 1 haben";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      const message = Object.values(errors).join(" • ");
      setSaveError(message);
      return;
    } else {
      setValidationErrors({});
    }

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Prepare the data for API call
      const updateData = { ...editedProduct };
      delete updateData.id;
      delete updateData.created_at;
      delete updateData.vhp_last_modified;
      delete updateData.vhp_archiving_date;
      delete updateData.created_by;
      delete updateData.last_modified_by;
      delete updateData.audit_trail;
      delete updateData.is_archived;
      delete updateData.archive_date;

      // When package is [] or "none" selected, send empty array (delivery products have no package)
      if (
        !updateData.paket?.length ||
        (Array.isArray(updateData.paket) && updateData.paket.includes("none"))
      ) {
        updateData.paket = [];
      } else if (Array.isArray(updateData.paket)) {
        updateData.paket = updateData.paket.filter((p) => p !== "none");
      }

      if (updateData.vhp_location) {
        // Convert to string if it's a number
        if (typeof updateData.vhp_location === "number") {
          updateData.vhp_location = updateData.vhp_location.toString();
        }
        // If it's already a string, keep it as is
      }

      // Normalize a quantity-config field into `{ default, configurations }`.
      // Drops rows where scenario or package was left blank (user added a row
      // but never filled it in). Default falls back to 0 (not 1/999) so
      // "applies to all" is a real setting the admin can pick.
      const normalizeQuantityConfig = (raw) => {
        if (raw == null) return { default: 0, configurations: [] };

        const source = Array.isArray(raw) ? { configurations: raw } : raw;

        const defaultVal = Number.parseInt(source.default, 10);
        const configs = Array.isArray(source.configurations)
          ? source.configurations
              .filter((c) => c && c.scenario && c.package)
              .map((c) => ({
                scenario: c.scenario,
                package: c.package,
                quantity: Number.isFinite(parseInt(c.quantity, 10))
                  ? parseInt(c.quantity, 10)
                  : 0,
              }))
          : [];

        return {
          default: Number.isFinite(defaultVal) ? defaultVal : 0,
          configurations: configs,
        };
      };

      if (updateData.mindestabnahme !== undefined) {
        updateData.mindestabnahme = normalizeQuantityConfig(
          updateData.mindestabnahme,
        );
      }
      if (updateData.maximalmenge !== undefined) {
        updateData.maximalmenge = normalizeQuantityConfig(
          updateData.maximalmenge,
        );
      }
      const updatedProduct = await productService.updateProduct(
        editedProduct.id,
        updateData,
      );
      setEditedProduct({ ...editedProduct, ...updatedProduct });
      setSaveSuccess(true);

      // Call the parent's onEdit callback to update the parent component
      onEdit(updatedProduct.id, { ...editedProduct, ...updatedProduct });

      // Hide success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Error saving product:", error);
      const data = error.response?.data ?? {};
      const detail = data.detail;
      const message =
        data.message ||
        (detail && typeof detail === "object" && detail.message
          ? detail.message
          : typeof detail === "string"
            ? detail
            : undefined) ||
        error.message ||
        "Failed to update product. Please try again.";
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedProduct({ ...product });
    setSaveError(null);
    setSaveSuccess(false);
    onClose();
  };

  const formatCurrency = (value) => {
    return value ? `${value} €` : "--";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "--";
    try {
      return new Date(dateString).toLocaleDateString("de-DE");
    } catch {
      return "--";
    }
  };

  // Configuration modal handlers.
  // Only load saved configurations; never auto-insert a blank row. The admin
  // adds rows explicitly via "+ Konfiguration hinzufügen". Uses `??` (not `||`)
  // so that a saved default of 0 is preserved.
  const handleMinConfigOpen = () => {
    if (
      editedProduct.mindestabnahme &&
      typeof editedProduct.mindestabnahme === "object"
    ) {
      setMinDefault(editedProduct.mindestabnahme.default ?? 0);
      setMinConfigurations(editedProduct.mindestabnahme.configurations || []);
    } else {
      setMinDefault(editedProduct.mindestabnahme ?? 0);
      setMinConfigurations([]);
    }
    setMinConfigModalOpen(true);
  };

  const handleMaxConfigOpen = () => {
    if (
      editedProduct.maximalmenge &&
      typeof editedProduct.maximalmenge === "object"
    ) {
      setMaxDefault(editedProduct.maximalmenge.default ?? 0);
      setMaxConfigurations(editedProduct.maximalmenge.configurations || []);
    } else {
      setMaxDefault(editedProduct.maximalmenge ?? 0);
      setMaxConfigurations([]);
    }
    setMaxConfigModalOpen(true);
  };

  const handleMinConfigSave = () => {
    const configData = {
      default: minDefault,
      configurations: minConfigurations,
    };
    handleInputChange("mindestabnahme", configData);
    setMinConfigModalOpen(false);
  };

  const handleMaxConfigSave = () => {
    const configData = {
      default: maxDefault,
      configurations: maxConfigurations,
    };
    handleInputChange("maximalmenge", configData);
    setMaxConfigModalOpen(false);
  };

  const addMinConfiguration = () => {
    setMinConfigurations([
      ...minConfigurations,
      {
        scenario: "",
        package: "",
        quantity: 1,
      },
    ]);
  };

  const addMaxConfiguration = () => {
    setMaxConfigurations([
      ...maxConfigurations,
      {
        scenario: "",
        package: "",
        quantity: 1,
      },
    ]);
  };

  const removeMinConfiguration = (index) => {
    // Ensure at least 1 row always exists
    if (minConfigurations.length > 1) {
      setMinConfigurations(minConfigurations.filter((_, i) => i !== index));
    }
  };

  const removeMaxConfiguration = (index) => {
    // Ensure at least 1 row always exists
    if (maxConfigurations.length > 1) {
      setMaxConfigurations(maxConfigurations.filter((_, i) => i !== index));
    }
  };

  const updateMinConfiguration = (index, field, value) => {
    const updated = [...minConfigurations];
    updated[index][field] = value;
    setMinConfigurations(updated);
  };

  const updateMaxConfiguration = (index, field, value) => {
    const updated = [...maxConfigurations];
    updated[index][field] = value;
    setMaxConfigurations(updated);
  };

  const getConfigurationSummary = (configurations) => {
    if (!configurations || configurations.length === 0) {
      return "Keine Konfiguration";
    }
    return `${configurations.length}`;
  };

  // Using centralized scenario and package options from scenarioConfig

  return (
    <div className="relative bg-white dark:bg-slate-800 w-full h-full shadow-xl border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden flex flex-col">
      {/* Overlay + loader when saving */}
      {saving && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/80 dark:bg-slate-900/90 backdrop-blur-[2px]">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 dark:border-blue-800 border-t-blue-600 dark:border-t-blue-400" />
            <p className="text-sm font-medium text-gray-700 dark:text-slate-300">
              Änderungen werden gespeichert...
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/50">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">
            {product.vhp_name || product.name || "Unnamed Product"}
          </h2>
          <div className="flex items-center gap-4 mt-2">
            <span className="text-sm text-gray-600 dark:text-slate-400">
              SKU: {product.vhp_sku || product.sku || "--"}
            </span>
            <span className="text-sm text-gray-600 dark:text-slate-400">
              Unit price:{" "}
              {formatCurrency(
                product.bruttopreis_pro_einheit || product.price_gross,
              )}
            </span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200 transition-colors p-2"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Save Status Messages */}
      {saveError && (
        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 dark:border-red-600 p-4 mx-4 mt-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-200">
                {saveError}
              </p>
              {Object.keys(validationErrors).length > 0 && (
                <ul className="mt-2 list-disc list-inside text-sm text-red-700 dark:text-red-200">
                  {Object.entries(validationErrors).map(([key, msg]) => (
                    <li key={key}>{msg}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {saveSuccess && (
        <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-400 dark:border-green-600 p-4 mx-4 mt-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-green-700 dark:text-green-200">
                Product updated successfully!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div
        className="flex-1 overflow-y-auto p-4"
        style={{ scrollbarWidth: "thin" }}
      >
        {/* Product Details Accordion */}
        <div className="border border-gray-200 dark:border-slate-600 rounded-lg">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
              About this product
            </h3>
            <ChevronDown
              className={`w-5 h-5 text-gray-500 dark:text-slate-400 transition-transform ${
                isExpanded ? "rotate-180" : ""
              }`}
            />
          </button>

          {isExpanded && (
            <div className="px-4 pb-4 space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="text-md font-semibold text-gray-800 dark:text-slate-200 border-b dark:border-slate-600 pb-2">
                  Basic Information
                </h4>

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={editedProduct.vhp_name || ""}
                      onChange={(e) =>
                        handleInputChange("vhp_name", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      SKU *
                    </label>
                    <input
                      type="number"
                      value={editedProduct.vhp_sku || ""}
                      onChange={(e) =>
                        handleInputChange("vhp_sku", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={editedProduct.description || ""}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:placeholder-slate-400"
                    placeholder="Enter product description..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    HTML Description
                  </label>
                  <textarea
                    value={editedProduct.html_description || ""}
                    onChange={(e) =>
                      handleInputChange("html_description", e.target.value)
                    }
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:placeholder-slate-400"
                    placeholder="Enter HTML formatted description..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Product type
                  </label>
                  <select
                    value={editedProduct.vhp_product_type || ""}
                    onChange={(e) =>
                      handleInputChange("vhp_product_type", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loadingProductTypes}
                  >
                    <option value="">Select type</option>
                    {productTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {loadingProductTypes && (
                    <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                      Loading product types...
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Type
                    </label>
                    <select
                      value={editedProduct.type || ""}
                      onChange={(e) =>
                        handleInputChange("type", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={loadingCategories}
                    >
                      <option value="">Select type</option>
                      {categories.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                    {loadingCategories && (
                      <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                        Loading categories...
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Status
                    </label>
                    <select
                      value={editedProduct.vhp_archiving_flag || ""}
                      onChange={(e) =>
                        handleInputChange("vhp_archiving_flag", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={loadingStatus}
                    >
                      <option value="">Select status</option>
                      {statusOptions.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                    {loadingStatus && (
                      <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                        Loading status...
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Rental Duration
                    </label>
                    <select
                      value={editedProduct.mietdauer || "einmalig"}
                      onChange={(e) =>
                        handleInputChange("mietdauer", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="einmalig">Einmalig</option>
                      <option value="täglich">Täglich</option>
                      <option value="wöchentlich">Wöchentlich</option>
                      <option value="monatlich">Monatlich</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Pricing Information */}
              <div className="space-y-4">
                <h4 className="text-md font-semibold text-gray-800 dark:text-slate-200 border-b dark:border-slate-600 pb-2">
                  Pricing Information
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Gross Price
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editedProduct.bruttopreis_pro_einheit || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "bruttopreis_pro_einheit",
                          e.target.value,
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Net Price
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editedProduct.vhp_net_price || ""}
                      onChange={(e) =>
                        handleInputChange("vhp_net_price", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Cost of Goods Sold
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editedProduct.vhp_cost_of_goods_sold || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "vhp_cost_of_goods_sold",
                          e.target.value,
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Unit
                    </label>
                    <input
                      type="text"
                      value={editedProduct.unit || ""}
                      onChange={(e) =>
                        handleInputChange("unit", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., Stück, m², h"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Net Rental Cost
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editedProduct.ek_miete_netto || ""}
                      onChange={(e) =>
                        handleInputChange("ek_miete_netto", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Gross Rental Cost
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editedProduct.ek_miete_brutto || ""}
                      onChange={(e) =>
                        handleInputChange("ek_miete_brutto", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Net Investment Cost
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editedProduct.ek_invest_netto || ""}
                      onChange={(e) =>
                        handleInputChange("ek_invest_netto", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Delivery/Pickup Cost
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editedProduct.cost_anlieferung_abholung || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "cost_anlieferung_abholung",
                          e.target.value,
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Setup/Teardown Cost
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editedProduct.cost_aufbau_abbau || ""}
                      onChange={(e) =>
                        handleInputChange("cost_aufbau_abbau", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Price Factor 1
                    </label>
                    <input
                      type="text"
                      value={editedProduct.price_factor1 || "1"}
                      onChange={(e) =>
                        handleInputChange("price_factor1", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Price Factor 2
                    </label>
                    <input
                      type="text"
                      value={editedProduct.price_factor2 || "1"}
                      onChange={(e) =>
                        handleInputChange("price_factor2", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Price Factor 3
                    </label>
                    <input
                      type="text"
                      value={editedProduct.price_factor3 || "1"}
                      onChange={(e) =>
                        handleInputChange("price_factor3", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Inventory Information */}
              <div className="space-y-4">
                <h4 className="text-md font-semibold text-gray-800 dark:text-slate-200 border-b dark:border-slate-600 pb-2">
                  Inventory Information
                </h4>

                <div className="flex flex-col gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Location
                    </label>
                    <select
                      value={editedProduct.vhp_location || ""}
                      onChange={(e) =>
                        handleInputChange("vhp_location", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={loadingLocations}
                    >
                      <option value="">Select location</option>
                      {locations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name} ({location.vhp_code})
                        </option>
                      ))}
                    </select>
                    {loadingLocations && (
                      <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                        Loading locations...
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Applicable For
                    </label>
                    <select
                      value={editedProduct.applicable_for || "both"}
                      onChange={(e) =>
                        handleInputChange("applicable_for", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={loadingApplicableFor}
                    >
                      {applicableForOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {loadingApplicableFor && (
                      <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                        Loading options...
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Mindestabnahme
                    </label>
                    <div className="flex items-center gap-3">
                      {editedProduct.mindestabnahme &&
                        typeof editedProduct.mindestabnahme === "object" &&
                        editedProduct.mindestabnahme.configurations &&
                        editedProduct.mindestabnahme.configurations.length >
                          0 && (
                          <span className="text-lg font-medium text-gray-900 dark:text-slate-100">
                            {getConfigurationSummary(
                              editedProduct.mindestabnahme.configurations,
                            )}
                          </span>
                        )}
                      <button
                        onClick={handleMinConfigOpen}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-slate-600 hover:bg-gray-200 dark:hover:bg-slate-500 rounded-lg transition-colors"
                      >
                        <Settings className="w-4 h-4 text-gray-600 dark:text-slate-300" />
                        <span className="text-sm text-gray-700 dark:text-slate-200">
                          Konfigurieren
                        </span>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Maximalmenge
                    </label>
                    <div className="flex items-center gap-3">
                      {editedProduct.maximalmenge &&
                        typeof editedProduct.maximalmenge === "object" &&
                        editedProduct.maximalmenge.configurations &&
                        editedProduct.maximalmenge.configurations.length >
                          0 && (
                          <span className="text-lg font-medium text-gray-900 dark:text-slate-100">
                            {getConfigurationSummary(
                              editedProduct.maximalmenge.configurations,
                            )}
                          </span>
                        )}
                      <button
                        onClick={handleMaxConfigOpen}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-slate-600 hover:bg-gray-200 dark:hover:bg-slate-500 rounded-lg transition-colors"
                      >
                        <Settings className="w-4 h-4 text-gray-600 dark:text-slate-300" />
                        <span className="text-sm text-gray-700 dark:text-slate-200">
                          Konfigurieren
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Setup Time (hours)
                    </label>
                    <input
                      type="text"
                      value={editedProduct.aufbauzeit_h || ""}
                      onChange={(e) =>
                        handleInputChange("aufbauzeit_h", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., 2.5"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Teardown Time (hours)
                    </label>
                    <input
                      type="text"
                      value={editedProduct.abbauzeit_h || ""}
                      onChange={(e) =>
                        handleInputChange("abbauzeit_h", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., 1.5"
                    />
                  </div>
                </div>
              </div>

              {/* Physical Properties */}
              <div className="space-y-4">
                <h4 className="text-md font-semibold text-gray-800 dark:text-slate-200 border-b dark:border-slate-600 pb-2">
                  Physical Properties
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Height
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editedProduct.height || ""}
                      onChange={(e) =>
                        handleInputChange("height", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="cm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Width
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editedProduct.width || ""}
                      onChange={(e) =>
                        handleInputChange("width", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="cm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Depth
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editedProduct.depth || ""}
                      onChange={(e) =>
                        handleInputChange("depth", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="cm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      GPS Latitude
                    </label>
                    <input
                      type="number"
                      step="0.000001"
                      value={editedProduct.gps_lat || ""}
                      onChange={(e) =>
                        handleInputChange("gps_lat", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      GPS Longitude
                    </label>
                    <input
                      type="number"
                      step="0.000001"
                      value={editedProduct.gps_lng || ""}
                      onChange={(e) =>
                        handleInputChange("gps_lng", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Orientation
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editedProduct.orientation || ""}
                      onChange={(e) =>
                        handleInputChange("orientation", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="degrees"
                    />
                  </div>
                </div>
              </div>

              {/* Booking & Display Settings */}
              <div className="space-y-4">
                <h4 className="text-md font-semibold text-gray-800 dark:text-slate-200 border-b dark:border-slate-600 pb-2">
                  Booking & Display Settings
                </h4>

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Bookable For Scenarios
                    </label>
                    <MultiSelectFilter
                      label="Select Scenarios"
                      options={scenarios}
                      selectedValues={
                        Array.isArray(editedProduct.bookable_for_scenarios)
                          ? editedProduct.bookable_for_scenarios
                          : []
                      }
                      onSelectionChange={handleScenarioSelectionChange}
                      searchPlaceholder="Search scenarios..."
                      isOpen={scenarioDropdownOpen}
                      onToggle={handleScenarioDropdownToggle}
                      filterKey="scenarios"
                    />
                    {loadingScenarios && (
                      <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                        Loading scenarios...
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Bookable For Original
                    </label>
                    <input
                      type="text"
                      value={editedProduct.bookable_for_original || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "bookable_for_original",
                          e.target.value,
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Original bookable scenarios"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Layout Position
                    </label>
                    <input
                      type="text"
                      value={editedProduct.layout_position || ""}
                      onChange={(e) =>
                        handleInputChange("layout_position", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., Pflicht"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Package
                    </label>
                    <MultiSelectFilter
                      label="Select Packages"
                      options={packageOptions}
                      selectedValues={
                        Array.isArray(editedProduct.paket) &&
                        editedProduct.paket.length === 0
                          ? ["none"]
                          : editedProduct.paket || []
                      }
                      onSelectionChange={handlePackageSelectionChange}
                      searchPlaceholder="Search packages..."
                      isOpen={packageDropdownOpen}
                      onToggle={handlePackageDropdownToggle}
                      filterKey="packages"
                    />
                    {loadingPackages && (
                      <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                        Loading packages...
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* External References */}
              <div className="space-y-4">
                <h4 className="text-md font-semibold text-gray-800 dark:text-slate-200 border-b dark:border-slate-600 pb-2">
                  External References
                </h4>

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      External ID
                    </label>
                    <input
                      type="text"
                      value={editedProduct.external_id || ""}
                      onChange={(e) =>
                        handleInputChange("external_id", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="External system ID"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Icon Asset
                    </label>
                    <input
                      type="text"
                      value={editedProduct.icon_asset || ""}
                      onChange={(e) =>
                        handleInputChange("icon_asset", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Icon asset reference"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Image URL
                    </label>
                    <input
                      type="url"
                      value={editedProduct.vhp_image_url || ""}
                      onChange={(e) =>
                        handleInputChange("vhp_image_url", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Image Caption
                    </label>
                    <input
                      type="text"
                      value={editedProduct.vhp_image_caption || ""}
                      onChange={(e) =>
                        handleInputChange("vhp_image_caption", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Image caption"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Rental Source URL
                    </label>
                    <input
                      type="url"
                      value={editedProduct.quelle_miete_url || ""}
                      onChange={(e) =>
                        handleInputChange("quelle_miete_url", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://example.com/rental-source"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Investment Source URL
                    </label>
                    <input
                      type="url"
                      value={editedProduct.quelle_invest_url || ""}
                      onChange={(e) =>
                        handleInputChange("quelle_invest_url", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://example.com/investment-source"
                    />
                  </div>
                </div>
              </div>

              {/* System Information */}
              <div className="space-y-4">
                <h4 className="text-md font-semibold text-gray-800 dark:text-slate-200 border-b dark:border-slate-600 pb-2">
                  System Information
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      ID
                    </label>
                    <p className="text-gray-900 dark:text-slate-200 py-2 text-sm">
                      {product.id || "--"}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Created
                    </label>
                    <p className="text-gray-900 dark:text-slate-200 py-2 text-sm">
                      {formatDate(product.created_at)}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Last Modified
                    </label>
                    <p className="text-gray-900 dark:text-slate-200 py-2 text-sm">
                      {formatDate(product.vhp_last_modified)}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Archiving Date
                  </label>
                  <p className="text-gray-900 dark:text-slate-200 py-2 text-sm">
                    {formatDate(product.vhp_archiving_date)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={handleCancel}
            disabled={saving}
            className="flex items-center px-4 py-2 text-sm bg-gray-500 dark:bg-slate-600 text-white rounded-lg hover:bg-gray-600 dark:hover:bg-slate-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center px-4 py-2 text-sm bg-blue-500 dark:bg-blue-600 text-white rounded-lg hover:bg-blue-600 dark:hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* Min Configuration Modal */}
      {minConfigModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl dark:border dark:border-slate-700 max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-slate-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                Mindestabnahme - Szenario-Paket-Konfiguration
              </h3>
              <button
                onClick={() => setMinConfigModalOpen(false)}
                className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                  Standardwert
                </label>
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
                  Gilt für alle Szenarien und Pakete, sofern unten keine
                  spezifische Konfiguration hinterlegt ist.
                </p>
                <input
                  type="number"
                  min="0"
                  value={minDefault}
                  onChange={(e) =>
                    setMinDefault(parseInt(e.target.value, 10) || 0)
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="space-y-3">
                {minConfigurations.map((config, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 border border-gray-200 dark:border-slate-600 rounded-lg"
                  >
                    <select
                      value={config.scenario}
                      onChange={(e) =>
                        updateMinConfiguration(
                          index,
                          "scenario",
                          e.target.value,
                        )
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Szenario wählen</option>
                      {SCENARIO_OPTIONS.filter((scenario) => {
                        // Filter out scenarios that are already selected in other rows
                        const isSelectedInOtherRows = minConfigurations.some(
                          (otherConfig, otherIndex) =>
                            otherIndex !== index &&
                            otherConfig.scenario === scenario.value,
                        );
                        return !isSelectedInOtherRows;
                      }).map((scenario) => (
                        <option key={scenario.value} value={scenario.value}>
                          {scenario.label}
                        </option>
                      ))}
                    </select>

                    <select
                      value={config.package}
                      onChange={(e) =>
                        updateMinConfiguration(index, "package", e.target.value)
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Paket wählen</option>
                      {PACKAGE_OPTIONS.map((pkg) => (
                        <option key={pkg.value} value={pkg.value}>
                          {pkg.label}
                        </option>
                      ))}
                    </select>

                    <input
                      type="number"
                      min="0"
                      value={config.quantity}
                      onChange={(e) =>
                        updateMinConfiguration(
                          index,
                          "quantity",
                          parseInt(e.target.value) || 0,
                        )
                      }
                      className="w-20 px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />

                    <button
                      onClick={() => removeMinConfiguration(index)}
                      disabled={minConfigurations.length === 1}
                      className={`p-2 rounded-lg transition-colors ${
                        minConfigurations.length === 1
                          ? "text-gray-300 dark:text-slate-500 cursor-not-allowed"
                          : "text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
                      }`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                {/* Add Configuration Button */}
                <button
                  onClick={addMinConfiguration}
                  className="w-full p-3 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg text-gray-600 dark:text-slate-400 hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center justify-center gap-2"
                >
                  <span className="text-lg">+</span>
                  Konfiguration hinzufügen
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-slate-600">
              <button
                onClick={() => setMinConfigModalOpen(false)}
                className="px-4 py-2 text-gray-600 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleMinConfigSave}
                className="px-4 py-2 bg-blue-500 dark:bg-blue-600 text-white rounded-lg hover:bg-blue-600 dark:hover:bg-blue-500 transition-colors"
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Max Configuration Modal */}
      {maxConfigModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl dark:border dark:border-slate-700 max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-slate-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                Maximalmenge - Szenario-Paket-Konfiguration
              </h3>
              <button
                onClick={() => setMaxConfigModalOpen(false)}
                className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                  Standardwert
                </label>
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
                  Gilt für alle Szenarien und Pakete, sofern unten keine
                  spezifische Konfiguration hinterlegt ist.
                </p>
                <input
                  type="number"
                  min="0"
                  value={maxDefault}
                  onChange={(e) =>
                    setMaxDefault(parseInt(e.target.value, 10) || 0)
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="space-y-3">
                {maxConfigurations.map((config, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 border border-gray-200 dark:border-slate-600 rounded-lg"
                  >
                    <select
                      value={config.scenario}
                      onChange={(e) =>
                        updateMaxConfiguration(
                          index,
                          "scenario",
                          e.target.value,
                        )
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Szenario wählen</option>
                      {SCENARIO_OPTIONS.filter((scenario) => {
                        // Filter out scenarios that are already selected in other rows
                        const isSelectedInOtherRows = maxConfigurations.some(
                          (otherConfig, otherIndex) =>
                            otherIndex !== index &&
                            otherConfig.scenario === scenario.value,
                        );
                        return !isSelectedInOtherRows;
                      }).map((scenario) => (
                        <option key={scenario.value} value={scenario.value}>
                          {scenario.label}
                        </option>
                      ))}
                    </select>

                    <select
                      value={config.package}
                      onChange={(e) =>
                        updateMaxConfiguration(index, "package", e.target.value)
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Paket wählen</option>
                      {PACKAGE_OPTIONS.map((pkg) => (
                        <option key={pkg.value} value={pkg.value}>
                          {pkg.label}
                        </option>
                      ))}
                    </select>

                    <input
                      type="number"
                      min="0"
                      value={config.quantity}
                      onChange={(e) =>
                        updateMaxConfiguration(
                          index,
                          "quantity",
                          parseInt(e.target.value) || 0,
                        )
                      }
                      className="w-20 px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />

                    <button
                      onClick={() => removeMaxConfiguration(index)}
                      disabled={maxConfigurations.length === 1}
                      className={`p-2 rounded-lg transition-colors ${
                        maxConfigurations.length === 1
                          ? "text-gray-300 dark:text-slate-500 cursor-not-allowed"
                          : "text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
                      }`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                {/* Add Configuration Button */}
                <button
                  onClick={addMaxConfiguration}
                  className="w-full p-3 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg text-gray-600 dark:text-slate-400 hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center justify-center gap-2"
                >
                  <span className="text-lg">+</span>
                  Konfiguration hinzufügen
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-slate-600">
              <button
                onClick={() => setMaxConfigModalOpen(false)}
                className="px-4 py-2 text-gray-600 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleMaxConfigSave}
                className="px-4 py-2 bg-blue-500 dark:bg-blue-600 text-white rounded-lg hover:bg-blue-600 dark:hover:bg-blue-500 transition-colors"
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailView;
